// Pure state for the map page's account widget. Unlike the admin panel's
// session machine, any linked player counts as signed in — the map shows
// who you are; admin rights are irrelevant here.
import type { Me } from '../api/models';

export type AccountState =
  | { kind: 'signed_out' }
  | { kind: 'probing' }
  | { kind: 'signed_in'; player: Me }
  | { kind: 'unlinked' } // session exists but no player row (pre-guard orphan)
  | { kind: 'error'; message: string };

export type AccountProbe =
  | { kind: 'player'; player: Me }
  | { kind: 'not_found' }
  | { kind: 'unauthorized' }
  | { kind: 'failed'; message: string };

export function applyAccountProbe(outcome: AccountProbe): AccountState {
  switch (outcome.kind) {
    case 'player':
      return { kind: 'signed_in', player: outcome.player };
    case 'not_found':
      return { kind: 'unlinked' };
    case 'unauthorized':
      return { kind: 'signed_out' };
    case 'failed':
      return { kind: 'error', message: outcome.message };
  }
}

// Sessions that shouldn't be kept: an unlinked (orphan) session gets
// dropped so the next attempt re-runs the Google flow.
export function shouldDropSession(state: AccountState): boolean {
  return state.kind === 'unlinked';
}

// Whether the widget must load the auth module at page load: an existing
// Supabase session in storage (sb-*-auth-token, plus the PKCE verifier key
// mid-flow) or OAuth params on the URL from a completed/failed redirect.
export function shouldInitAuth(
  storageKeys: string[],
  search: string,
  hash: string,
): boolean {
  if (storageKeys.some((k) => /^sb-.+-auth-token/.test(k))) return true;
  const merged = `${search.replace(/^\?/, '')}&${hash.replace(/^#/, '')}`;
  const params = new URLSearchParams(merged);
  return params.has('code') || params.has('error') || params.has('error_description');
}

// Whether to offer the admin-panel link under the signed-in name. Purely
// cosmetic — /v1/admin enforces the role server-side — but regular players
// shouldn't see a door they can't open.
export function showAdminLink(player: Me): boolean {
  return player.role === 'admin';
}

// "Lady Padme (@tiggus)" / "Lord @tiggus"
export function accountLabel(player: Me): string {
  const title = player.title === 'lady' ? 'Lady' : 'Lord';
  return player.display_name?.trim()
    ? `${title} ${player.display_name} (@${player.username})`
    : `${title} @${player.username}`;
}
